import React, { useEffect, useState } from "react";
import { Loader2, Plus, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/pages/crm/LeadForm";

const Chips = ({ list, onRemove, label }) => (
  <div className="flex flex-wrap gap-1.5">
    {list.length === 0 && <span className="text-xs text-muted-foreground">No {label} added.</span>}
    {list.map((e) => (
      <span key={e} className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary-soft px-2.5 py-1 text-xs text-primary">
        {e}
        <button type="button" onClick={() => onRemove(e)} className="hover:text-destructive"><X className="h-3 w-3" /></button>
      </span>
    ))}
  </div>
);

export const EmailDialog = ({ kind, doc, open, onOpenChange, onSent }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newTo, setNewTo] = useState("");
  const [newCc, setNewCc] = useState("");

  useEffect(() => {
    if (!open || !doc) return;
    setData(null);
    api.get(`/documents/${kind}/${doc.id}/email-preview`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(errMsg(e)); onOpenChange(false); });
  }, [open, doc, kind, onOpenChange]);

  const send = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/documents/${kind}/${doc.id}/email`, data);
      if (res.data.status === "sent") toast.success("Email sent successfully");
      else if (res.data.status === "outbox") toast.warning("SMTP is not configured. The email is saved in the Email Outbox in Admin Settings.");
      else toast.error(`Email failed: ${res.data.error}`);
      onSent?.();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Send {doc?.number}</DialogTitle>
          <DialogDescription>
            Recipients and content come from your saved template. You can edit anything before sending.
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="grid h-40 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-5">
            <Field label="To">
              <div className="space-y-2">
                <Chips list={data.to} label="recipients" onRemove={(e) => setData({ ...data, to: data.to.filter((x) => x !== e) })} />
                <div className="flex gap-2">
                  <Input value={newTo} onChange={(e) => setNewTo(e.target.value)} placeholder="add@email.com" data-testid="email-to-input" />
                  <Button type="button" variant="outline" onClick={() => { if (newTo) { setData({ ...data, to: [...data.to, newTo] }); setNewTo(""); } }}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Field>

            <Field label="CC" hint="Default CC recipients come from Admin Settings.">
              <div className="space-y-2">
                <Chips list={data.cc} label="CC recipients" onRemove={(e) => setData({ ...data, cc: data.cc.filter((x) => x !== e) })} />
                <div className="flex gap-2">
                  <Input value={newCc} onChange={(e) => setNewCc(e.target.value)} placeholder="cc@email.com" />
                  <Button type="button" variant="outline" onClick={() => { if (newCc) { setData({ ...data, cc: [...data.cc, newCc] }); setNewCc(""); } }}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Field>

            <Field label="Subject">
              <Input value={data.subject} onChange={(e) => setData({ ...data, subject: e.target.value })} data-testid="email-subject" />
            </Field>
            <Field label="Message">
              <Textarea rows={10} value={data.body} onChange={(e) => setData({ ...data, body: e.target.value })} className="font-sans text-sm" />
            </Field>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={busy || !data} data-testid="email-send">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
